import * as React from "react";
import styled from "styled-components";
import { useIsoContext } from "src/isoContext";

interface Props {
  tabs: TabSpec[];
  selected?: string;
  onSelect?(tabId: string): void;
}

interface TabSpec {
  id: string;
  label: string;
  render(): React.ReactNode;
}

export function Tabs({ selected, onSelect, tabs }: Props) {
  const [tab, setTab] = React.useState(
    (selected && tabs.find((t) => t.id === selected)?.id) || tabs[0].id,
  );
  const { isLarge } = useIsoContext();

  const isControlled = selected !== undefined && onSelect !== undefined;
  const selectedTab = isControlled ? selected : tab;
  const setSelectedTab = (tabId: string) => {
    if (isControlled) {
      onSelect(tabId);
    } else {
      setTab(tabId);
    }
  };

  return (
    <>
      {!isLarge && (
        <TabList>
          {tabs.map((t) => (
            <Tab key={t.id} $active={t.id === selectedTab} onClick={() => setSelectedTab(t.id)}>
              {t.label}
            </Tab>
          ))}
        </TabList>
      )}
      <Columns>
        {tabs.map((t) => (
          <Column $isLarge={isLarge} $active={selectedTab === t.id}>
            {t.render()}
          </Column>
        ))}
      </Columns>
    </>
  );
}

const Columns = styled.div`
  display: flex;
  gap: 20px;
`;

const TabList = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const Tab = styled.div<{ $active: boolean }>`
  flex-basis: 0;
  flex-grow: 1;
  text-align: center;
  padding: 10px 0 8px;
  cursor: pointer;
  border-bottom: 2px solid var(--box-color-contrast);
  ${({ $active }) =>
    $active &&
    `
    border-bottom-color: var(--theme-color-primary);
  `}
`;

const Column = styled.div<{ $isLarge: boolean; $active: boolean }>`
  flex-basis: 0;
  flex-grow: 1;
  ${({ $isLarge, $active }) => !$isLarge && !$active && "display: none;"}
`;
